import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api-client";
import type {
  DataImportSummary,
  ImportPreviewResponse,
  ImportProfileRequest,
  ImportProfileResponse,
  MappingDraft,
} from "../../types/data-import.types.js";

export type {
  DataImportSummary,
  ImportPreviewResponse,
  ImportProfileResponse,
  MappingDraft,
} from "../../types/data-import.types.js";

const QK = ["data-imports"] as const;

export function useDataImports(refetchInterval = 5_000) {
  return useQuery<DataImportSummary[]>({
    queryKey: QK,
    queryFn: () => api.get<DataImportSummary[]>("/api/v1/data-imports"),
    refetchInterval,
  });
}

const TERMINAL_IMPORT_STATUSES = new Set<DataImportSummary["status"]>([
  "COMPLETED",
  "FAILED",
  // Spec 082 — desfeita é estado terminal: não há mais nada acontecendo para o poll acompanhar.
  "REVERTED",
]);

export function useDataImport(id: string, poll = false) {
  return useQuery<DataImportSummary>({
    queryKey: ["data-imports", id],
    queryFn: () => api.get<DataImportSummary>(`/api/v1/data-imports/${id}`),
    enabled: !!id,
    // Poll só enquanto o status é não-terminal; sem isto, um erro transitório de
    // rede num refetch em background após COMPLETED/FAILED derrubava a tela de resultado já
    // exibida com sucesso para uma tela de erro genérica.
    refetchInterval: poll
      ? (query) =>
          query.state.data && TERMINAL_IMPORT_STATUSES.has(query.state.data.status) ? false : 2_000
      : undefined,
  });
}

interface UploadResp {
  import: DataImportSummary;
  uploadUrl: string;
}

export function useCreateUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      filename: string;
      lineId?: string;
      origin?: "FILE" | "ERP" | "MES";
      referenceAt?: string;
    }) => api.post<UploadResp>("/api/v1/data-imports/upload", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
  });
}

/**
 * A pré-visualização depende de aba e linha de cabeçalho, então elas entram na chave: trocar a aba
 * precisa buscar de novo, e um `staleTime` infinito com chave fixa devolveria a aba anterior.
 */
export function useImportPreview(
  id: string | null,
  opts: { sheetName?: string | null; headerRow?: number } = {}
) {
  const params = new URLSearchParams();
  if (opts.sheetName) params.set("sheetName", opts.sheetName);
  if (opts.headerRow) params.set("headerRow", String(opts.headerRow));
  const qs = params.toString();
  return useQuery<ImportPreviewResponse>({
    queryKey: ["data-imports", id, "preview", opts.sheetName ?? null, opts.headerRow ?? 1],
    queryFn: () =>
      api.get<ImportPreviewResponse>(`/api/v1/data-imports/${id}/preview${qs ? `?${qs}` : ""}`),
    enabled: !!id,
  });
}

const PROFILES_QK = ["import-profiles"] as const;

export function useImportProfiles() {
  return useQuery<ImportProfileResponse[]>({
    queryKey: PROFILES_QK,
    queryFn: () => api.get<ImportProfileResponse[]>("/api/v1/import-profiles"),
  });
}

export function useSaveImportProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ImportProfileRequest) =>
      api.post<ImportProfileResponse>("/api/v1/import-profiles", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILES_QK }),
  });
}

export function useImportTransition() {
  const qc = useQueryClient();
  return {
    context: useMutation({
      meta: { skipErrorToast: true },
      mutationFn: (id: string) =>
        api.post<DataImportSummary>(`/api/v1/data-imports/${id}/context`, {}),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: QK });
        void qc.invalidateQueries({ queryKey: ["data-imports", id] });
      },
    }),
    mapping: useMutation({
      meta: { skipErrorToast: true },
      mutationFn: ({
        id,
        draft,
        saveAsProfile,
      }: {
        id: string;
        draft: MappingDraft;
        saveAsProfile?: string;
      }) =>
        api.post<DataImportSummary>(`/api/v1/data-imports/${id}/mapping`, {
          columnMapping: draft.columnMapping,
          conflictPolicy: draft.conflictPolicy,
          dateColumn: draft.dateColumn,
          ...(draft.sheetName ? { sheetName: draft.sheetName } : {}),
          headerRow: draft.headerRow,
          originRules: draft.originRules,
          ...(saveAsProfile ? { saveAsProfile } : {}),
        }),
      onSuccess: (_, vars) => {
        void qc.invalidateQueries({ queryKey: QK });
        void qc.invalidateQueries({ queryKey: ["data-imports", vars.id] });
        // O mapeamento muda o que a pré-visualização mostra: sem isto a amostra continua a de antes.
        void qc.invalidateQueries({ queryKey: ["data-imports", vars.id, "preview"] });
        void qc.invalidateQueries({ queryKey: PROFILES_QK });
      },
    }),
    validate: useMutation({
      meta: { skipErrorToast: true },
      mutationFn: (id: string) =>
        api.post<DataImportSummary>(`/api/v1/data-imports/${id}/validate`, {}),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: QK });
        void qc.invalidateQueries({ queryKey: ["data-imports", id] });
      },
    }),
    /**
     * Spec 082 — desfazer apaga execuções. A invalidação é LARGA de propósito: o histórico que
     * some atravessa produção, OEE, painel e ordens, e deixar qualquer uma dessas telas com o
     * número antigo é pior do que recarregar tudo.
     */
    revert: useMutation({
      meta: { skipErrorToast: true },
      mutationFn: (id: string) =>
        api.post<{ runsDeleted: number }>(`/api/v1/data-imports/${id}/revert`, {}),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: QK });
        void qc.invalidateQueries({ queryKey: ["data-imports", id] });
        void qc.invalidateQueries({ queryKey: ["production"] });
        void qc.invalidateQueries({ queryKey: ["oee"] });
        void qc.invalidateQueries({ queryKey: ["dashboard", "overview"] });
        void qc.invalidateQueries({ queryKey: ["production-orders"] });
        // Spec 084 — desfazer leva junto as bateladas importadas, os valores delas e as origens
        // que apontavam para elas. Por prefixo: a herdeira afetada pode ser de outra linha.
        void qc.invalidateQueries({ queryKey: ["batches"] });
        void qc.invalidateQueries({ queryKey: ["batch"] });
      },
    }),
    execute: useMutation({
      meta: { skipErrorToast: true },
      mutationFn: (id: string) =>
        api.post<DataImportSummary>(`/api/v1/data-imports/${id}/execute`, {}),
      onSuccess: (_, id) => {
        void qc.invalidateQueries({ queryKey: QK });
        void qc.invalidateQueries({ queryKey: ["data-imports", id] });
        // A importação ESCREVE produção: as mesmas telas que o desfazer invalida.
        void qc.invalidateQueries({ queryKey: ["production"] });
        void qc.invalidateQueries({ queryKey: ["oee"] });
        void qc.invalidateQueries({ queryKey: ["dashboard", "overview"] });
        void qc.invalidateQueries({ queryKey: ["production-orders"] });
        // Spec 084 — a importação também cria BATELADAS, com marca e origem.
        void qc.invalidateQueries({ queryKey: ["batches"] });
        void qc.invalidateQueries({ queryKey: ["batch"] });
      },
    }),
  };
}

export async function putFileToSignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    credentials: "include",
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    Sentry.captureException(new Error("putFileToSignedUrl falhou"), {
      extra: { status: res.status, body: txt },
    });
    throw new Error("Não foi possível enviar o arquivo. Tente novamente.");
  }
}
